import { createContext, useCallback, useContext, useEffect, useRef, useState, useMemo } from "react";
import { useData } from "../DataContext/DataProvider";
import {
  checkInstagram,
  checkNativeShare,
  checkWhatsapp,
} from "./UIContextUtils";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import { DateTime } from "luxon";
import { useUser } from "../UserContext/UserProvider";

const UIContext = createContext();

const THEME_PREFERENCE_KEY = "themePreference";

const getSystemDarkMode = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

// Explicit user choice ("dark" | "light"), or null if they never toggled.
// Legacy "darkMode" key defaulted to false, so only "true" is a real choice.
const getStoredThemePreference = () => {
  try {
    const stored = localStorage.getItem(THEME_PREFERENCE_KEY);
    if (stored === "dark" || stored === "light") return stored;
    if (localStorage.getItem("darkMode") === "true") return "dark";
  } catch (error) {
    // Ignore storage access errors (private mode, etc.)
  }
  return null;
};

const getInitialDarkMode = () => {
  const stored = getStoredThemePreference();
  return stored ? stored === "dark" : getSystemDarkMode();
};

export const UIProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);
  const [likedFilter, setLikedFilter] = useState(false);
  const [posterFilter, setPosterFilter] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTargetTab, setSettingsTargetTab] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [doubleCollapsed, setDoubleCollapsed] = useState(false);
  const [bottomSheetRef, setBottomSheetRef] = useState(null); //this is the ref to the bottom sheet component in the outlet context
  const [mainMapRef, setMainMapRef] = useState(null); //this is the ref to the main map component in the outlet context
  const { globalConfig } = useData();
  const categories = globalConfig?.categories || [];
  const [likedEvents, setLikedEvents] = useState([]);
  const [activeCategories, setActiveCategories] = useState(
    categories.map((c) => c.id)
  );
  const [availableApps, setAvailableApps] = useState({});
  const [filterDateActive, setFilterDateActive] = useState(false);
  const { user } = useUser();
  const mainContentRef = useRef(null);
  const resetMapViewportRef = useRef(null);

  const registerResetMapViewport = useCallback((fn) => {
    resetMapViewportRef.current = fn;
  }, []);

  const resetMapViewport = useCallback(() => {
    resetMapViewportRef.current?.();
  }, []);

  const checkAvailableApps = async () => {
    const apps = {
      instagram: await checkInstagram(),
      whatsapp: await checkWhatsapp(),
      native: await checkNativeShare(),
    };
    setAvailableApps((prev) => ({ ...prev, ...apps }));
  };

  useEffect(() => {
    checkAvailableApps();
    try {
      const stored = getStoredThemePreference();
      if (stored) localStorage.setItem(THEME_PREFERENCE_KEY, stored);
      localStorage.removeItem("darkMode");
    } catch (error) {
      // Ignore storage access errors (private mode, etc.)
    }
  }, []);

  // Only called from user-facing toggles; this is what makes the choice stick.
  const toggleDarkMode = useCallback(() => {
    const next = !darkMode;
    try {
      localStorage.setItem(THEME_PREFERENCE_KEY, next ? "dark" : "light");
    } catch (error) {
      // Ignore storage access errors (private mode, etc.)
    }
    setDarkMode(next);
  }, [darkMode]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event) => {
      if (getStoredThemePreference()) return;
      setDarkMode(event.matches);
    };

    if (media.addEventListener) {
      media.addEventListener("change", onChange);
    } else {
      media.addListener(onChange);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", onChange);
      } else {
        media.removeListener(onChange);
      }
    };
  }, []);

  const likeEvent = async (event) => {
    // find the event in the likedEvents array
    let likedEvent = likedEvents.find((e) => e._id === event._id);
    let existsInLikedEvents = true;
    // if the event is not in the likedEvents array, add it
    if (!likedEvent) {
      existsInLikedEvents = false;
      likedEvent = {
        ...event,
      };
    }

    //toggle the likes array of the event which is an array of usr ids
    likedEvent.likes = likedEvent.likes?.includes(user?._id)
      ? likedEvent.likes.filter((id) => id !== user?._id)
      : [...likedEvent.likes, user?._id];

    if (existsInLikedEvents) {
      setLikedEvents(
        likedEvents.map((e) => (e._id === likedEvent._id ? likedEvent : e))
      );
    } else {
      setLikedEvents([...likedEvents, likedEvent]);
    }
  };

  useEffect(() => {
    document.body.classList.toggle("dark", darkMode);
    document.body.classList.toggle("default", !darkMode);
  }, [darkMode]);

  // Memoize context value to prevent unnecessary re-renders
  // Only recreate when actual dependencies change
  const value = useMemo(() => ({
    availableApps,
    searchOpen,
    setSearchOpen,
    settingsOpen,
    setSettingsOpen,
    settingsTargetTab,
    setSettingsTargetTab,
    collapsed,
    setCollapsed,
    doubleCollapsed,
    setDoubleCollapsed,
    bottomSheetRef,
    setBottomSheetRef,
    mainMapRef,
    setMainMapRef,
    mainContentRef,
    filterDateActive,
    setFilterDateActive,
    likedFilter,
    setLikedFilter,
    posterFilter,
    setPosterFilter,
    activeCategories,
    setActiveCategories,
    likeEvent,
    likedEvents,
    darkMode,
    setDarkMode,
    toggleDarkMode,
    registerResetMapViewport,
    resetMapViewport,
  }), [
    availableApps,
    searchOpen,
    settingsOpen,
    settingsTargetTab,
    collapsed,
    doubleCollapsed,
    bottomSheetRef,
    mainMapRef,
    mainContentRef,
    filterDateActive,
    likedFilter,
    posterFilter,
    activeCategories,
    likeEvent,
    likedEvents,
    darkMode,
    toggleDarkMode,
    registerResetMapViewport,
    resetMapViewport,
  ]);

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUIContext = () => {
  return useContext(UIContext);
};

export default UIContext;
